import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import axios from "axios";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { flowRight } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";
import { TypeAttributes } from "rsuite/esm/internals/types";
import {
    Container,
    Content,
    Footer as RsuiteFooter,
    FormInstance,
    Header,
    Heading,
    HStack,
    Message,
    Text,
    toaster,
    Toggle,
} from "rsuite";

import i18n from "utils/i18n";
import constants from "utils/constants";
import { HUB_NAMES } from "utils/serverless-hub";
import { formatDateSlash } from "utils/date-helper";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import {
    getTenantDisplayName,
    getTenantId,
    getTenantMoniker,
} from "store/environments/tenants/tenants-selectors";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import { Tenant } from "store/environments/tenants/studio/studio-types";
import { SecretExpiration } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import { User } from "store/environments/tenants/users/users-types";
import { Application } from "store/azure/ad/ad-types";
import {
    StudioWorkflow,
    StudioWorkflowType,
    WorkflowStatus,
    WorkflowStepStatus,
} from "store/workflows/workflows-types";

import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";

import { getErrorMessage } from "api/defaults";

import {
    createTenant,
    loadStudio,
    loadStudioWorkflows,
    rotateClientSecret,
} from "api/environments/tenants/studio-api";
import { loadUsers } from "api/environments/tenants/users/users-api";

import useRequestCancellation from "hooks/useRequestCancellation";
import useRefreshPage from "hooks/useRefreshPage";
import withWebSocket, {
    WithWebSocketProps,
} from "hocs/withWebSocket/withWebSocket";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";

import { isInProgress } from "components/Workflows/workflows-helper";

import TenantForm, { TenantFormValue } from "./Forms/TenantForm";
import AppForm, { ApplicationFormValue } from "./Forms/AppForm";

import Footer from "components/Common/Footer/Footer";
import ConfirmModal, {
    ConfirmationMode,
} from "components/Common/ConfirmModal/ConfirmModal";
import ProgressButton from "components/Workflows/Button/Button";

import "./Studio.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithWebSocketProps {
    title?: string;

    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

const Studio: React.FC<Props> = ({
    title = i18n.t("tenantConfig.studio.header"),
    webSocketPayload,
    showLoader,
    showError,
    addWebSocketUserId,
    addWebSocketHabName,
}) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();

    const tenantFormRef = useRef<FormInstance<Record<string, any>>>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();

    const { environmentId, tenantId } = params;

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const activeTenantMoniker = useSelector((state: RootState): string =>
        getTenantMoniker(state)
    );

    const activeTenantName = useSelector((state: RootState): string =>
        getTenantDisplayName(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [isOn, setIsOn] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const [tenant, setTenant] = useImmer<Tenant | null>(null);
    const [application, setApplication] = useImmer<Application | null>(null);
    const [secretExpiration, setSecretExpiration] =
        useImmer<SecretExpiration | null>(null);

    const [users, setUsers] = useImmer<ReadonlyArray<User>>([]);
    const [selectedUser, setSelectedUser] = useImmer<User | null>(null);

    const [studioWorkflows, setStudioWorkflows] = useImmer<StudioWorkflow[]>(
        []
    );

    const [integrationWorkflow, setIntegrationWorkflow] =
        useImmer<StudioWorkflow | null>(null);

    const [rotationWorkflow, setRotationWorkflow] =
        useImmer<StudioWorkflow | null>(null);

    const [rotationConfirmOpen, setRotationConfirmOpen] = useState(false);

    const tenantFormDisabled = initialized || !isOn;

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const tenantFormValue: TenantFormValue = {
        displayName: tenant?.displayName ?? activeTenantName,
        user: selectedUser,
        link: tenant?.link ?? null,
    };

    const applicationFormValue: ApplicationFormValue = {
        displayName: application?.displayName ?? t("unknown"),
        appId: application?.appId ?? t("unknown"),
        passwordEndDateTime: application?.passwordCredentials?.[0]?.endDateTime
            ? formatDateSlash(
                  new Date(application?.passwordCredentials?.[0]?.endDateTime)
              )
            : null,
        link: application?.link ?? null,
    };

    const getMessage = (): { type: TypeAttributes.Status; text: string } => {
        if (!tenant && application) {
            return {
                type: "warning",
                text: t("tenantConfig.studio.tenantWarningMessage"),
            };
        }

        return {
            type: "info",
            text: t("tenantConfig.studio.infoMessage"),
        };
    };

    const message = getMessage();

    const pushMessage = (type: TypeAttributes.Status, text: string) => {
        toaster.push(
            <Message showIcon type={type}>
                {text}
            </Message>,
            toastOptions
        );
    };

    const upsertStudioWorkflow = (workflow: StudioWorkflow) => {
        setStudioWorkflows((draft) => {
            const index = draft.findIndex((w) => w.id === workflow.id);

            if (index !== -1) {
                draft[index] = {
                    ...workflow,
                    steps: [...workflow.steps],
                };
            } else {
                draft.push({
                    ...workflow,
                    steps: [...workflow.steps],
                });
            }
        });
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchStudio(), fetchStudioWorkflows(), fetchUsers()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                tenantFormRef.current?.cleanErrors();
                showLoader?.(false);

                setDataFetched(true);
            });
    };

    const fetchStudioWorkflows = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadStudioWorkflows(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setStudioWorkflows(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchStudio = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadStudio(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setTenant(payload.data.tenant ?? null);
                setApplication(payload.data.application ?? null);
                setSecretExpiration(payload.data.secretExpiration ?? null);
                setSelectedUser(payload.data.user ?? null);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchUsers = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadUsers(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setUsers(payload.data);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const initTenant = async () => {
        if (!isOn) {
            return null;
        }

        const payload = {
            id: activeTenantId,
            name: activeTenantName,
            moniker: activeTenantMoniker,
            data: { user: selectedUser },
        };

        try {
            const [result, status] = await createTenant(
                activeEnvironmentId,
                activeTenantId,
                payload,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(result), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        const valid = tenantFormRef?.current?.check() ?? true;

        const permitted = dataFetched && valid;

        if (!permitted) {
            return;
        }

        showLoader?.(true);
        const promises = [initTenant()];

        Promise.all(promises).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.studio.successMessage");
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                toastOptions
            );
        });
    };

    const verifyTenant = (tenant: Tenant | null) => {
        return !!(tenant && tenant.id && tenant.users && tenant.apps);
    };

    const verifyApplication = (application: Application | null) => {
        return !!(
            application &&
            application.appId &&
            application.passwordCredentials
        );
    };

    const handleTenantFormChange = (data: Record<string, any>) => {
        const updated = data as TenantFormValue;

        const user =
            users.find(
                (u) => u.userPrincipalName === updated.user?.userPrincipalName
            ) ?? null;

        setSelectedUser(user);

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleToggleChange = (checked: boolean) => {
        if (initialized) {
            return;
        }

        setIsOn(checked);
        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleTerminated = () => {
        fetchData();
    };

    const handleRotateClientSecretClick = () => {
        setRotationConfirmOpen(true);
    };

    const handleRotationCancel = () => {
        setRotationConfirmOpen(false);
    };

    const handleRotationConfirm = async () => {
        setRotationConfirmOpen(false);
        showLoader?.(true);

        const payload = {
            id: activeTenantId,
            name: activeTenantName,
            moniker: activeTenantMoniker,
            data: { tenant, application, user: selectedUser },
        };

        try {
            const [result, status] = await rotateClientSecret(
                activeEnvironmentId,
                activeTenantId,
                payload,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK && result) {
                upsertStudioWorkflow(result);
                pushMessage(
                    "success",
                    t("tenantConfig.studio.clientSecret.startedSuccessfully")
                );
            } else if (status === constants.statusCode.Conflict) {
                pushMessage(
                    "warning",
                    t("tenantConfig.studio.clientSecret.workflowInProgress")
                );
            } else {
                pushMessage("error", getErrorMessage(String(result), status));
            }
        } catch (e) {
            if (!axios.isCancel(e)) {
                pushMessage("error", String(e));
            }
        } finally {
            showLoader?.(false);
        }
    };

    const rotationDisabled =
        !verifyTenant(tenant) ||
        !verifyApplication(application) ||
        isInProgress(integrationWorkflow?.status) ||
        isInProgress(rotationWorkflow?.status);

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (studioWorkflows) {
            addWebSocketHabName(HUB_NAMES.STUDIO_HANDLING);
            addWebSocketUserId(activeTenantId);
        }
    }, [activeTenantId, studioWorkflows]);

    useEffect(() => {
        const workflow =
            studioWorkflows.find(
                (w) => w.type === StudioWorkflowType.Integration
            ) ?? null;

        setIntegrationWorkflow(workflow);

        const rotation =
            studioWorkflows.find(
                (w) => w.type === StudioWorkflowType.RotateClientSecret
            ) ?? null;

        setRotationWorkflow(rotation);
    }, [studioWorkflows]);

    useEffect(() => {
        const tenantInitialized = verifyTenant(tenant ?? null);
        const applicationInitialized = verifyApplication(application ?? null);

        const inProgress = isInProgress(integrationWorkflow?.status);

        const initialized =
            (tenantInitialized && applicationInitialized) || inProgress;

        setInitialized(initialized);
        setIsOn(initialized);
    }, [integrationWorkflow, tenant, application]);

    useEffect(() => {
        if (webSocketPayload) {
            try {
                const payload = JSON.parse(webSocketPayload) as StudioWorkflow;

                if (payload) {
                    upsertStudioWorkflow(payload);

                    if (payload.status === WorkflowStatus.Succeeded) {
                        const stepFailed = payload.steps.some(
                            (s) => s.status === WorkflowStepStatus.Failed
                        );

                        if (stepFailed) {
                            pushMessage(
                                "warning",
                                t("tenantConfig.studio.warningMessage")
                            );
                        } else if (
                            payload.type ===
                            StudioWorkflowType.RotateClientSecret
                        ) {
                            pushMessage(
                                "success",
                                t(
                                    "tenantConfig.studio.clientSecret.completedSuccessfully"
                                )
                            );
                        }

                        fetchData();
                    }
                }
            } catch (error) {
                console.error("Failed to parse payload:", error);
            }
        }
    }, [webSocketPayload]);

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && activeEnvironmentId) {
            dispatch(fetchTenants(activeEnvironmentId, tenantId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!isOn || dataFetched) {
            tenantFormRef.current?.cleanErrors();
        }
    }, [isOn, dataFetched]);

    useEffect(() => {
        if (!isOn) {
            dispatch(setIsUnsavedChangeAvailable(false));
        }
    }, [isOn]);

    return (
        dataFetched && (
            <Container className="studio__container">
                <Header>
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="studio__content" ref={containerRef}>
                    <Message showIcon type={message.type}>
                        {message.text}
                    </Message>

                    <HStack
                        style={{ margin: "1rem 0" }}
                        justifyContent={"flex-start"}
                        spacing={0}
                    >
                        <HStack.Item alignSelf={"center"}>
                            <Toggle
                                checked={isOn}
                                disabled={initialized}
                                className={cn("studio__toggle", {
                                    "studio__toggle--initialized": initialized,
                                })}
                                checkedChildren={t("toggler.onTitle")}
                                unCheckedChildren={t("toggler.offTitle")}
                                onChange={handleToggleChange}
                            />
                        </HStack.Item>

                        <HStack.Item
                            style={{ marginLeft: "0.5rem" }}
                            alignSelf={"center"}
                        >
                            <Text>
                                {t("tenantConfig.studio.toggleControl")}
                            </Text>
                        </HStack.Item>

                        <HStack.Item alignSelf={"center"}>
                            <ProgressButton
                                environmentId={environmentId}
                                tenantId={tenantId}
                                workflow={integrationWorkflow}
                                onTerminated={handleTerminated}
                            />
                        </HStack.Item>
                    </HStack>

                    <TenantForm
                        style={{ margin: "2rem 0 0" }}
                        formValue={tenantFormValue}
                        users={users}
                        formRef={tenantFormRef}
                        containerRef={containerRef}
                        disabled={tenantFormDisabled}
                        onChange={(formValue) =>
                            handleTenantFormChange(formValue)
                        }
                    />

                    {application && (
                        <AppForm
                            style={{ margin: "1.5rem 0 0" }}
                            formValue={applicationFormValue}
                            environmentId={environmentId}
                            tenantId={tenantId}
                            rotationWorkflow={rotationWorkflow}
                            rotationDisabled={rotationDisabled}
                            secretExpiration={secretExpiration}
                            onRotateClientSecret={handleRotateClientSecretClick}
                            onRotationTerminated={handleTerminated}
                        />
                    )}

                    <ConfirmModal
                        open={rotationConfirmOpen}
                        mode={ConfirmationMode.Destructive}
                        message={t(
                            "tenantConfig.studio.clientSecret.confirmMessage"
                        )}
                        onCancel={handleRotationCancel}
                        onConfirm={handleRotationConfirm}
                    />

                    <Text className="studio_footer">
                        <sup className={"studio__sub"}>*</sup>
                        {t("tenantConfig.studio.requiredFields")}
                    </Text>
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={() => handleSubmit()} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError, withWebSocket)(Studio);

export default Component;
